/**
 * The one place the browser talks to the API.
 *
 * `api.md` §1 defines three auth contexts — public, device, user — and says
 * *"the client sends the right token per call: `bearerFor(context)`"*. This
 * module owns that, the base URL, and the single most consequential piece of
 * knowledge in the offline stack: **which failures are worth retrying.**
 */

import { refreshUserToken } from './refresh'

/**
 * The base every request is resolved against.
 *
 * ⚠️ EXPORTED FOR MEDIA URLS, NOT FOR REQUESTS. `voiceProvider.ts` hands a URL
 * to an `<audio>` element rather than going through `request()`, so it needs
 * this same base — and must not derive its own from the env var, or the two
 * drift.
 *
 * 🔴 IT EMITTED A BARE `/api/v1/...` UNTIL PRODUCTION PUT THE PWA AND THE API
 * ON DIFFERENT ORIGINS. The comment below still says "in production the app and
 * the API are the same origin" — that stopped being true when the PWA moved to
 * Cloudflare Pages. Every voice line then resolved against school.kalqy.in,
 * where Pages answers unknown paths with index.html and a 200: the browser got
 * HTML where it expected audio, played nothing, and reported no error at all.
 */
export function baseUrl(): string {
  /*
   * Blank in dev, so requests go to a RELATIVE `/api/v1/...` and through Vite's
   * proxy to :3001 (frontend/.env.example, vite.config.ts). In production the
   * app and the API are the same origin.
   *
   * 🔴 A device token is a 365-day bearer credential for a child's account
   * (security.md §5). Sending it to whatever host an env var happened to name is
   * how a credential leaves the building, so the value is read once, here, and
   * `request()` refuses caller-supplied absolute URLs outright.
   */
  return import.meta.env.VITE_API_BASE_URL ?? ''
}

/** `api.md` §7 — URI versioning, so an installed PWA never breaks mid-session. */
const API_PREFIX = '/api/v1'

/**
 * The rotation route, named here because two things need it: the 401 path
 * below resolves it into a URL for `refresh.ts`, and the same path must be
 * excluded from that handling so a rotation which itself 401s cannot recurse.
 */
const REFRESH_PATH = '/auth/refresh'

/** Long enough for a slow school connection, short enough not to hang a boot. */
const DEFAULT_TIMEOUT_MS = 15_000

/**
 * `api.md` §1 defines three: public, device, user.
 *
 * 🔴 `user` ARRIVED IN §8.4 AND CARRIES NOTHING YET, ON PURPOSE. Staff login
 * is Unit 9, so no caller can supply a user token — every teacher screen
 * therefore issues a real request and receives a real 401. That is the honest
 * state, and it is the one thing that stops D1–D7 becoming *"eight hardcoded
 * children while working endpoints sit uncalled"*: the screens are wired, and
 * the missing piece is a credential rather than a call.
 */
/**
 * An operator's chosen reporting period, as `YYYY-MM-DD` bounds.
 *
 * 🔴 DEFINED ONCE, HERE, BECAUSE THREE ROUTES TAKE IT AND THEY MUST NOT DRIFT.
 * A dashboard whose tiles were filtered over three subtly different windows
 * would be a screen no two numbers on which are comparable, and the drift would
 * be invisible — every figure would look plausible on its own.
 */
export interface DateWindow {
  from: string
  to: string
}

/**
 * `?from=…&to=…`, or nothing at all.
 *
 * ⚠️ BOTH OR NEITHER, MIRRORING THE SERVER. `window.dto.ts` answers a lone
 * bound with a 400 rather than picking the other end itself, so sending half a
 * window is a broken screen, not a wider one.
 *
 * ⚠️ `prefix` EXISTS BECAUSE ONE CALLER ALREADY HAS A QUERY STRING.
 * `/superadmin/growth` may carry `days`, and the schemas are `.strict()` — a
 * second `?` would arrive as a parameter name nobody declared and be refused.
 */
export function windowQuery(window?: DateWindow, prefix: '?' | '&' = '?'): string {
  if (window === undefined) return ''
  const from = encodeURIComponent(window.from)
  const to = encodeURIComponent(window.to)
  return `${prefix}from=${from}&to=${to}`
}

export type AuthContext = 'public' | 'device' | 'user'

/**
 * The verbs the API actually uses.
 *
 * ⚠️ `PATCH` ARRIVED IN §10's PLATFORM TIER, AND IT IS A WIDENING RATHER THAN A
 * NEW BEHAVIOUR — `fetch` passes the verb through untouched, so no existing
 * caller changes. It is here because three platform routes are genuinely
 * PATCHes (`schools/:id/status`, `schools/:id/region`, `content/:id/state`):
 * each names a target state on a resource that already exists.
 *
 * 🔴 THE ALTERNATIVE WAS A CAST AT THE CALL SITE, and that is why this line
 * moved instead. `method: 'PATCH' as 'POST'` type-checks, works, and makes the
 * one module in the app that owns *"which verb goes to which route"* stop
 * describing the truth — and the next person to add a verb copies the cast
 * rather than the fix.
 */
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface RequestOptions {
  method?: HttpMethod
  body?: unknown
  context?: AuthContext
  /** The device token, supplied by the caller — this module stores nothing. */
  token?: string
  timeoutMs?: number
  signal?: AbortSignal
  /**
   * What the body is. Defaults to `'json'`, which is every route but one.
   *
   * 🔴 `'text'` EXISTS FOR X9's `GET /audit.csv`, AND THE ALTERNATIVE WAS
   * WORSE. Without it `response.json()` throws on a `text/csv` body and the
   * caller receives `ApiError(0, 'Malformed response body')` — classified
   * **retryable**, so an offline-aware caller would retry a download that
   * succeeded, forever.
   *
   * ⚠️ THE OTHER ALTERNATIVE WAS BUILDING THE CSV IN THE BROWSER, which
   * `school.ts` does for NCF coverage. It is right there and wrong here: the
   * audit CSV's cells carry `summary` and `actorLabel` — caller-influenced text
   * — and the server escapes them against spreadsheet formula injection with
   * `csvCell`. A second escaper in the browser is two implementations of a
   * security rule, which the backend consolidated for exactly this reason.
   */
  expect?: 'json' | 'text'
  /**
   * Extra request headers — today only `Idempotency-Key`, so a money or
   * onboarding write the operator double-clicks is applied once.
   *
   * 🔴 THEY CAN NEVER REPLACE A HEADER THIS MODULE OWNS. `Authorization`,
   * `Accept` and `Content-Type` (in any letter case) are dropped from this
   * map before it is merged: the bearer is chosen by `context` and nothing
   * else, which is the rule that keeps a 365-day device token from being sent
   * where a caller happened to put it.
   */
  headers?: Record<string, string>
}

/** Headers `request()` owns; a caller's `headers` may never set them. */
const OWNED_HEADERS = new Set(['authorization', 'accept', 'content-type'])

/**
 * Everything a caller needs to decide what to do next.
 *
 * `status` is 0 for "the request never got an answer" — offline, DNS, timeout,
 * a connection dropped mid-flight.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * 🔴 THE SERVER'S MACHINE-READABLE REASON, AND THE ONLY THING READ OUT OF AN
     * ERROR BODY. `null` whenever the server did not send one, which is most
     * responses.
     *
     * It exists because a status alone is ambiguous on exactly the screens where
     * being wrong costs the most: `POST /superadmin/schools` answers 409 both
     * for a duplicate school NAME and for a taken admin EMAIL, and F2 guessed
     * "name" for both — telling an operator to rename a school that was fine,
     * with no rename able to fix it.
     *
     * ⚠️ IT IS NEVER THE `message`. See `readConflictCode`.
     */
    readonly code: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * 🔴 SCREAMING_SNAKE OR NOTHING — THIS PATTERN IS THE PRIVACY GUARANTEE, NOT A
 * TIDINESS RULE.
 *
 * The reason error bodies went unread is real and unchanged: *"a 400 from
 * `/auth/device` echoes validation messages about fields that carry a child's
 * name, and this is a browser console on a shared classroom laptop."* A code
 * matching this cannot be a name, an address, a sentence, or any value a human
 * typed — it is a closed-set identifier the server chose. Anything else is
 * dropped, and the screen falls back to its own generic sentence.
 */
const CONFLICT_CODE_PATTERN = /^[A-Z][A-Z0-9_]{2,48}$/

/**
 * ⚠️ FAILS TO `null` ON EVERY PATH — a body that is not JSON, is JSON but not an
 * object, has no `code`, or has one that is not a slug. An error path that could
 * itself throw would replace a useful 409 with a parse error.
 */
async function readConflictCode(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.clone().json()
    if (typeof body !== 'object' || body === null) return null
    const code = (body as { code?: unknown }).code
    return typeof code === 'string' && CONFLICT_CODE_PATTERN.test(code) ? code : null
  } catch {
    return null
  }
}

/**
 * 🔴 THE RULE §4.6 MUST NOT RE-DERIVE, AND THE TRAP INSIDE IT.
 *
 * `offline-sync.md` §5 says the outbox **discards on 4xx**, giving `400`/`403`
 * as its examples — *"a permanently-invalid item must not poison the queue"*.
 * Read literally, that discards **429 too**, and a 429 will succeed on retry.
 *
 * That is not a hypothetical: the moment twenty classroom devices drain their
 * queues is exactly the moment a rate limit trips (§4.2 sized the ingest limit
 * around it). An outbox that discards every 4xx would therefore **delete a
 * classroom's morning of events at the instant the wifi came back**, silently,
 * with every layer behaving as designed.
 *
 * So the classification lives here, next to the code that produced the status,
 * rather than being inferred twice from the same ambiguous sentence.
 */
export function isRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false

  // No answer at all: offline, timeout, connection reset. The wifi comes back.
  if (error.status === 0) return true

  // 408 Request Timeout · 425 Too Early · 429 Too Many Requests — the request
  // was fine, the moment was not.
  if (error.status === 408 || error.status === 425 || error.status === 429) return true

  // Everything else in 4xx is a permanent statement about the request itself:
  // a malformed body, a rejected enum, a session that is not this player's.
  // Retrying it forever is what turns a buffer into a wall.
  if (error.status >= 400 && error.status < 500) return false

  // "Online but the API was 502 for a minute" — the case offline-sync.md §4
  // added the periodic flush for.
  return true
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    context = 'public',
    token,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    expect = 'json',
  } = options

  /*
   * 🔴 Relative paths only. A caller-supplied absolute URL would let any future
   * feature — a config value, a deep link, a copied snippet — post a child's
   * device token to another origin.
   */
  if (/^[a-z][a-z0-9+.-]*:|^\/\//i.test(path)) {
    throw new ApiError(0, 'Absolute URLs are not allowed')
  }

  /*
   * ✅ ONE ATTEMPT, AS A FUNCTION, BECAUSE A `user` CALL NOW GETS A SECOND ONE.
   *
   * Everything a replay must not reuse is built inside: a spent
   * `AbortSignal.timeout` fires immediately the second time round, and the
   * bearer is the entire reason for trying again.
   */
  const attempt = async (bearer: string | undefined): Promise<Response> => {
    // `Accept` follows what the caller says it can read, so a proxy or a future
    // content-negotiating route cannot hand back a body this function will drop.
    const headers: Record<string, string> = {}
    for (const [name, value] of Object.entries(options.headers ?? {})) {
      if (!OWNED_HEADERS.has(name.toLowerCase())) headers[name] = value
    }
    headers.Accept = expect === 'text' ? 'text/csv, text/plain' : 'application/json'
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    /*
     * `bearerFor(context)` (api.md §1). The token travels in the Authorization
     * header and nowhere else: a credential in a URL ends up in access logs and
     * Referer headers, and this one lives for a year.
     *
     * A `public` call sends no token even when one is available — `/auth/device`
     * is the registration call, and sending a credential to a route that does not
     * read it is how credentials end up in logs that were never meant to hold any.
     */
    /*
     * A user (access) token is 15 minutes and a device token is 365 days; both
     * travel the same way, and neither is ever put in a URL. `public` still
     * sends nothing even when a token is available.
     */
    if ((context === 'device' || context === 'user') && bearer) {
      headers.Authorization = `Bearer ${bearer}`
    }

    const timeout = AbortSignal.timeout(timeoutMs)
    const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout

    try {
      return await fetch(`${baseUrl()}${API_PREFIX}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
        // No cookies: the device context is a bearer token, and a kids product
        // must not be embeddable or credentialed cross-site.
        credentials: 'omit',
      })
    } catch {
      // Offline, DNS failure, timeout, connection reset — indistinguishable from
      // here and identical in consequence: try again later.
      throw new ApiError(0, 'Network request failed')
    }
  }

  let response = await attempt(token)

  /*
   * 🔴 ROTATE ONCE ON 401, THEN REPLAY. THIS IS THE FIFTEEN-MINUTE LOGOUT.
   *
   * `JWT_ACCESS_TTL` is 15 minutes and nothing renewed it, so every staff member
   * and parent was signed out a quarter of an hour after signing in — the token
   * aged out, the next call 401'd, and `isRetryable` (correctly) refused to
   * retry a 4xx. The status was never transient; the credential simply needed
   * rotating, which is a different repair and belongs here rather than in the
   * retry classifier. `refresh.ts` owns the rotation and the single-flight lock
   * that stops six parallel loads racing each other's `jti`.
   *
   * ⚠️ ONCE, AND ONLY FOR `user`. A second 401 after a fresh token is a real
   * authorisation failure — the account was deactivated, the row is scoped to
   * somebody else — and retrying that is a loop. `device` tokens live a year and
   * have no rotation route; `public` sends no credential to rotate.
   *
   * ⚠️ AND NEVER FOR THE ROTATION ROUTE ITSELF. `refresh.ts` uses a bare `fetch`
   * so it cannot reach this branch today; the guard is what keeps that true if
   * `refreshStaffSession` in `auth.ts` is ever wired to an explicit "renew now".
   */
  if (response.status === 401 && context === 'user' && path !== REFRESH_PATH) {
    const rotated = await refreshUserToken(`${baseUrl()}${API_PREFIX}${REFRESH_PATH}`)
    if (rotated !== null) response = await attempt(rotated)
  }

  if (!response.ok) {
    /*
     * 🔴 THE STATUS IS THE DIAGNOSIS; THE MESSAGE IS STILL NOT READ.
     *
     * A 400 from `/auth/device` echoes validation messages about fields that
     * carry a child's name, and this is a browser console on a shared classroom
     * laptop. That rule stands: `message` never leaves the response.
     *
     * ⚠️ WHAT CHANGED IS NARROW AND DELIBERATE — a single `code` field, read
     * only when it matches `CONFLICT_CODE_PATTERN`, so what reaches the app is a
     * closed-set slug and never a value. The status alone was ambiguous exactly
     * where it mattered: two different 409s on one form, and the screen guessing
     * which. Retry-or-discard still keys off the status and is unaffected.
     */
    throw new ApiError(
      response.status,
      `Request failed with ${String(response.status)}`,
      await readConflictCode(response),
    )
  }

  // 202 and 204 carry no body by design (§4.2, §4.3) — asking for JSON would
  // throw on the happy path.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  try {
    // 🔴 `.text()` cannot fail the way `.json()` can, so a text caller has no
    // "malformed body" path — the bytes either arrived or the fetch threw.
    if (expect === 'text') return (await response.text()) as T
    return (await response.json()) as T
  } catch {
    // A 2xx with an unreadable body: succeeded, but the caller cannot use it.
    // Retryable, because a proxy interstitial is the usual cause.
    throw new ApiError(0, 'Malformed response body')
  }
}
